/**
 * keepscroll — 화면을 다시 그려도 **보고 있던 자리를 잃지 않게** 한다.
 *
 * 왜 필요한가
 * ----------
 * 트리는 무엇을 고칠 때마다 `window.location.reload()` 로 화면을 다시 받는다
 * (서버가 그리는 것이 진실이고, DOM 을 손으로 기우면 sort_order·depth 가
 * 어긋날 여지가 생긴다 — `tree.ts` 참고). 그런데 트리가 고정 높이 상자 안에서
 * 스크롤하게 되면서 **다시 그릴 때마다 맨 위로 튄다.** 스무 번째 항목의 이름을
 * 고치면 그 줄이 화면 밖으로 사라진다.
 *
 * 기존 서비스에도 같은 장치가 있었다(`keepscroll` — AJAX 로 목록을 갈아 끼운 뒤
 * 저장해 둔 scrollTop 으로 되돌린다). 그 발상을 그대로 가져오되 두 가지를 고쳤다:
 *
 *   ① **`setTimeout(300)` 으로 기다리지 않는다.** 300ms 는 추측이라 내용이 늦게
 *      오면 틀리고, 빠르면 그만큼 화면이 늦게 자리를 잡는다. 여기서는 매 프레임
 *      확인하다가 **되돌릴 만큼 높이가 확보되면 그때** 되돌린다.
 *   ② **화면마다 키가 따로다.** 전역 변수 하나에 담으면 목록이 둘인 화면에서
 *      서로 덮어쓴다. `경로 + 이름` 으로 나눈다.
 *
 * 쓰는 법 — 마크업에 한 줄:
 *
 *     <div class="nx-tree-box" data-simplebar data-nx-keepscroll="tree">…</div>
 *
 * ★ 브라우저 기본 복원과 겹치지 않는다. 브라우저는 `window` 의 스크롤만
 *   되돌리는데, 여기서 다루는 것은 **상자 안쪽**이라 서로 건드리지 않는다.
 *
 * 언제 되돌리나 — 아무 때나 되돌리면 안 된다
 * ------------------------------------------
 * 메뉴에서 이 화면을 **새로 눌러 들어왔는데** 예전 자리로 튀면 그건 고장이다.
 * 되돌려야 하는 것은 두 경우뿐이다:
 *
 *   · **새로고침**(트리가 고친 뒤 부르는 `location.reload()`)
 *   · **뒤로/앞으로**
 *
 * 이 둘은 추측할 필요가 없다 — Navigation Timing 이 알려준다
 * (`performance.getEntriesByType('navigation')[0].type`).
 * 그 밖의 진입(`navigate`)은 저장값을 **지우고 맨 위에서 시작**한다.
 *
 * ★ "되돌린 뒤 지운다"(한 번 쓰고 버리기)는 쓰지 않는다. 트리에서 **연달아 두 번**
 *   고치면 두 번째에 자리가 사라진다 — 첫 번째 복원에서 값을 지웠는데 그 사이
 *   사용자가 스크롤을 안 해 다시 저장될 일이 없기 때문이다. 진입 종류로 가르는
 *   편이 정확하고, 값은 그대로 두어도 된다.
 */

const PREFIX = 'nx.keepscroll';

/** 너무 오래된 값은 되돌리지 않는다 — 한참 뒤에 다시 온 사람에게는 맨 위가 맞다. */
const MAX_AGE_MS = 10 * 60 * 1000;

/** 되돌릴 자리가 나올 때까지 확인하는 최대 프레임 수(약 0.5초). */
const MAX_FRAMES = 30;

interface Saved {
  top: number;
  t: number;
}

/**
 * 실제로 스크롤하는 요소.
 *
 * SimpleBar 가 붙으면 상자가 아니라 **안쪽 래퍼**가 스크롤한다.
 * 아직 안 붙었으면(스크립트 차단·로드 전) 상자 자체가 스크롤한다.
 */
function scrollerOf(box: HTMLElement): HTMLElement {
  return box.querySelector<HTMLElement>('.simplebar-content-wrapper') ?? box;
}

function keyOf(box: HTMLElement): string {
  // 쿼리까지 넣는다 — `?slot=service1` 과 `?slot=service2` 는 **다른 데이터**다.
  // 경로만 쓰면 슬롯을 바꿔 들어왔을 때 남의 자리로 되돌린다.
  const where = window.location.pathname + window.location.search;
  return `${PREFIX}:${where}:${box.dataset.nxKeepscroll || ''}`;
}

/**
 * 이번 진입이 **자리를 되돌려야 하는 진입**인가.
 *
 *   reload       — 트리가 고친 뒤 부르는 `location.reload()`
 *   back_forward — 뒤로/앞으로
 *   navigate     — 링크·메뉴로 새로 들어옴 → 맨 위에서 시작한다
 */
function shouldRestore(): boolean {
  try {
    const nav = window.performance?.getEntriesByType?.('navigation')?.[0] as
      | PerformanceNavigationTiming
      | undefined;
    const type = nav?.type;
    // 알 수 없으면 되돌리지 않는다 — 튀는 것보다 맨 위가 낫다.
    return type === 'reload' || type === 'back_forward';
  } catch {
    return false;
  }
}

function clear(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* 무시 */
  }
}

function read(key: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as Saved;
    if (typeof v?.top !== 'number') return null;
    if (Date.now() - (v.t || 0) > MAX_AGE_MS) return null;
    return v.top;
  } catch {
    return null; // 사파리 프라이빗 모드 등 — 없는 셈 친다
  }
}

function write(key: string, top: number): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ top, t: Date.now() } as Saved));
  } catch {
    /* 저장 못 해도 화면은 동작한다 */
  }
}

/**
 * 저장해 둔 자리로 되돌린다.
 *
 * 내용이 아직 덜 자랐으면 되돌려도 소용없다(스크롤 가능 높이가 모자라 0으로
 * 잘린다). 그래서 **매 프레임 확인**하다가 높이가 확보되면 그때 한 번 되돌린다.
 * 웹폰트·이미지가 늦게 와서 높이가 더 자라는 경우가 있어, 되돌린 뒤에도
 * 몇 프레임 더 지켜보다 어긋나면 한 번 더 맞춘다.
 */
function restore(box: HTMLElement, top: number): void {
  if (top <= 0) return;
  let frames = 0;
  let applied = false;

  const tick = (): void => {
    const el = scrollerOf(box);
    const room = el.scrollHeight - el.clientHeight;
    if (room >= top - 2) {
      // `- 2`: 소수 scrollTop vs 정수 높이(9-106) — 브라우저가 클램핑한다
      el.scrollTop = top;
      applied = true;
    }
    // 자리가 잡혔고 실제로 그 값이 들어갔으면 끝낸다.
    if (applied && Math.abs(el.scrollTop - top) < 2) return;
    if (++frames < MAX_FRAMES) window.requestAnimationFrame(tick);
  };
  window.requestAnimationFrame(tick);
}

function watch(box: HTMLElement, restoring: boolean): void {
  const key = keyOf(box);
  if (restoring) {
    const saved = read(key);
    if (saved !== null) restore(box, saved);
  } else {
    // 새로 들어온 것이다 — 남아 있던 자리는 버리고 맨 위에서 시작한다.
    clear(key);
  }

  // 스크롤은 초당 수십 번 온다 — 프레임당 한 번만 저장한다.
  let queued = false;
  const onScroll = (): void => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      write(key, scrollerOf(box).scrollTop);
    });
  };

  // ★ SimpleBar 는 DOM 을 감시하다 붙으므로 **안쪽 래퍼가 나중에 생긴다.**
  //   상자에서 올라오는 스크롤 이벤트를 캡처 단계로 받으면 래퍼가 언제
  //   생기든 상관없다(스크롤 이벤트는 버블링하지 않으므로 캡처로 받는다).
  box.addEventListener('scroll', onScroll, { passive: true, capture: true });
}

/** 이미 이어 둔 영역인지 표시 — 같은 요소에 두 번 붙으면 저장이 두 배로 돈다. */
const WATCHED = 'nxKeepscrollBound';

function watchIn(root: ParentNode, restoring: boolean): void {
  root.querySelectorAll<HTMLElement>('[data-nx-keepscroll]').forEach((box) => {
    if (box.dataset[WATCHED] === '1') return;
    box.dataset[WATCHED] = '1';
    watch(box, restoring);
  });
}

/** `data-nx-keepscroll` 이 붙은 스크롤 영역을 전부 잇는다. */
export function initKeepScroll(): void {
  const restoring = shouldRestore();
  watchIn(document, restoring);

  // ★ 팝업 조각은 나중에 들어온다. 부팅 때 한 번만 훑으면 그 안의 목록은
  //   자리를 못 지킨다. `modal.ts` 가 알려주면 그때 붙인다.
  //   조각은 **새로 열린 것**이므로 되돌리지 않는다(restoring=false).
  document.addEventListener('nx:modal:load', (ev) => {
    const root = (ev as CustomEvent<{ root?: ParentNode }>).detail?.root;
    watchIn(root ?? document, false);
  });
}

/* ==========================================================================
 * F197 (사장님 지시 2026-08-06) — **창 스크롤** 되돌리기: 목록에서 상세로
 * 들어갔다가 [목록으로] 로 돌아오면 보던 자리에 서 있어야 한다.
 *
 * 위 keepscroll 과 다른 문제다: 저것은 상자 안 스크롤을 reload/뒤로가기에서
 * 지키고, 이것은 **창 스크롤**을 **앞으로 가는 링크 복귀**(navigate — 브라우저
 * 기본 복원이 안 도는 진입)에서 지킨다.
 *
 * 장치는 표식 한 장이다:
 *   ① 모든 화면이 떠날 때(pagehide) 제 주소 키로 창 스크롤을 저장한다.
 *   ② [목록으로] 류 링크(`data-nx-return`)를 **맨클릭**하면 "이 주소로
 *      돌아간다" 표식을 남긴다(새 탭 열기는 표식을 안 남긴다 — 이 탭이
 *      이동하지 않는데 표식이 남으면 다음 진입이 엉뚱하게 튄다).
 *   ③ 도착한 화면의 주소가 표식과 같으면 저장값으로 되돌린다. 표식은
 *      한 번 쓰고 지운다 — 그다음 메뉴로 새로 들어오면 맨 위가 맞다.
 * ========================================================================== */

const WIN_PREFIX = 'nx.winscroll';
const RETURN_KEY = 'nx.return-to';

function winKey(): string {
  return `${WIN_PREFIX}:${window.location.pathname}${window.location.search}`;
}

/* 9-103(사장님 실측 2026-08-07): 뒤로가기는 복원되는데 [목록으로]는 맨
 * 위로 갔다 — 뒤로가기는 **브라우저가 원소 단위 스크롤까지** 통째로
 * 복원해 주는 반면, 우리 장치는 창(window)만 저장했기 때문이다. 레이아웃
 * 에 따라 실제 스크롤러가 창이 아니라 본문 컨테이너일 수 있다(넓은
 * 화면에서 재현). 그래서 창과 함께 **스크롤돼 있는 요소 전부**를 저장
 * 하고, 도착하면 같은 표식(태그+클래스)의 요소에 되돌린다. */
interface WinSaved {
  top: number;
  t: number;
  els?: { m: string; top: number }[];
}

function markerOf(el: Element): string {
  return `${el.tagName}.${Array.from(el.classList).sort().join('.')}`;
}

function scrolledEls(): { m: string; top: number }[] {
  const out: { m: string; top: number }[] = [];
  document.querySelectorAll('*').forEach((el) => {
    if (el.scrollTop > 0 && el.clientHeight > 0) {
      out.push({ m: markerOf(el), top: el.scrollTop });
    }
  });
  return out.slice(0, 20); // 폭주 방지 — 실제로는 한두 개다
}

function writeWin(): void {
  try {
    const v: WinSaved = { top: window.scrollY, t: Date.now(), els: scrolledEls() };
    window.sessionStorage.setItem(winKey(), JSON.stringify(v));
  } catch {
    /* 저장 못 해도 화면은 동작한다 */
  }
}

function readWin(): WinSaved | null {
  try {
    const raw = window.sessionStorage.getItem(winKey());
    if (!raw) return null;
    const v = JSON.parse(raw) as WinSaved;
    if (typeof v?.top !== 'number') return null;
    if (Date.now() - (v.t || 0) > MAX_AGE_MS) return null;
    return v;
  } catch {
    return null;
  }
}

/** 표식과 같은(태그+클래스) 요소를 찾아 되돌린다 — 창과 같은 대기 전략. */
function restoreEls(els: { m: string; top: number }[]): void {
  els.forEach(({ m, top }) => {
    if (top <= 0) return;
    const tag = m.split('.')[0];
    const el = Array.from(document.getElementsByTagName(tag)).find(
      (e) => markerOf(e) === m,
    );
    if (!el) {
      console.debug('[nx] return-scroll: 요소 못 찾음', m);
      return;
    }
    const moved = userMovedGuard();
    let frames = 0;
    const tick = (): void => {
      if (moved()) return;
      // 창과 같은 `- 2` 허용 오차(9-106) — 소수 scrollTop vs 정수 높이
      if (el.scrollHeight - el.clientHeight >= top - 2 && Math.abs(el.scrollTop - top) >= 2) {
        el.scrollTop = top;
      }
      if (++frames < HOLD_FRAMES) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });
}

/** 창 스크롤 복원 — 문서가 아직 짧으면 프레임마다 기다렸다 되돌린다(위 restore 와 같은 발상).
 *
 *  ★ `behavior:'instant'` 를 못 박는다 — Zynix 가 `html{scroll-behavior:
 *    smooth}` 를 전역으로 켜 두어서, 그냥 scrollTo 는 복원이 **1초짜리
 *    미끄럼**이 된다(크로미움 실측: 150ms 시점 132px, 1150ms 에야 1200px).
 *    instant 는 CSS smooth 를 무시하고 즉시 놓는다(F199~F201). */
/* 9-104: 복원을 지키는 시간을 늘린다(약 3초) — 웹폰트·CSS 가 늦게 와서
 * 레이아웃이 0.5초 뒤에 다시 자리를 흔들면 30프레임으로는 놓친다
 * (사장님 환경 재현 실패의 남은 후보). 단 **사용자가 직접 움직이면 즉시
 * 양보**한다(휠·터치·키) — 복원이 사용자와 싸우면 안 된다. */
const HOLD_FRAMES = 180;

function userMovedGuard(): () => boolean {
  let moved = false;
  const on = (): void => {
    moved = true;
  };
  const opts = { passive: true, once: true } as AddEventListenerOptions;
  window.addEventListener('wheel', on, opts);
  window.addEventListener('touchstart', on, opts);
  window.addEventListener('keydown', on, opts);
  return () => moved;
}

function restoreWindow(top: number): void {
  if (top <= 0) return;
  const moved = userMovedGuard();
  let frames = 0;
  let applied = false;
  const tick = (): void => {
    if (moved()) return;
    const doc = document.documentElement;
    // ★ `- 2` 허용 오차(9-106, 사장님 콘솔로 확정): 브라우저 배율에서
    //   scrollY 는 소수(736.30…)인데 scrollHeight·innerHeight 는 정수
    //   반올림이라, **맨 아래에서 저장한 값은 영원히 "높이 미달"** 이 되어
    //   복원을 포기했다(`창 복원 포기 736.30…` 실측). scrollTo 는 어차피
    //   범위를 넘으면 끝으로 클램핑하므로 오차를 두고 놓는 쪽이 맞다.
    if (doc.scrollHeight - window.innerHeight >= top - 2) {
      if (Math.abs(window.scrollY - top) >= 2) {
        window.scrollTo({ top, left: 0, behavior: 'instant' as ScrollBehavior });
      }
      applied = true;
    }
    if (++frames < HOLD_FRAMES) window.requestAnimationFrame(tick);
    else if (!applied) console.debug('[nx] return-scroll: 창 복원 포기', top);
  };
  window.requestAnimationFrame(tick);
}

export function initReturnScroll(): void {
  // 번들 신구 판별용 표식(9-103) — 콘솔에 이 줄이 없으면 옛 app.js 다.
  console.debug('[nx] return-scroll v9-106');
  // ① 떠날 때 저장 — pagehide 는 링크 이동·닫기 모두에서 돈다.
  //    창 + 스크롤된 요소 전부(9-103).
  window.addEventListener('pagehide', () => {
    writeWin();
  });
  // 진단(9-104): 다음 화면에서 이 로그를 보면 어디서 끊겼는지 바로 보인다.
  window.addEventListener('pagehide', () => {
    console.debug('[nx] return-scroll: 저장', winKey(), window.scrollY);
  });

  // ② [목록으로] 류 링크가 표식을 남긴다(맨클릭만 — 새 탭은 제외).
  document.addEventListener('click', (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || (e as MouseEvent).button !== 0) return;
    const a = (e.target as Element).closest?.('a[data-nx-return]') as
      | HTMLAnchorElement
      | null;
    if (!a) return;
    try {
      window.sessionStorage.setItem(RETURN_KEY, a.pathname + a.search);
      console.debug('[nx] return-scroll: 표식 저장', a.pathname + a.search);
    } catch {
      /* 저장 못 하면 그냥 맨 위 — 동작은 계속된다 */
    }
  });

  // ③ 표식이 내 주소면 되돌린다 — 한 번 쓰고 지운다.
  //    ③′ 표식이 없어도 **뒤로/앞으로·새로고침**(back_forward·reload)으로
  //    도착했다면 되돌린다(9-101, 사장님 지시 "사이트 전체에서 유지").
  //    [목록으로] 버튼(표식)만이 아니라 브라우저 뒤로가기로 목록에 돌아와도
  //    자리가 지켜진다. 브라우저 기본 복원은 레이아웃이 자라기 전에 먼저
  //    돌아 0 으로 잘릴 수 있는데, 우리 복원은 높이가 확보될 때까지
  //    프레임마다 기다렸다 놓는다(restoreWindow). 메뉴·링크로 **새로**
  //    들어온 진입(navigate)은 여전히 맨 위 — 되돌리지 않는다.
  try {
    const mark = window.sessionStorage.getItem(RETURN_KEY);
    const here = window.location.pathname + window.location.search;
    let saved: WinSaved | null = null;
    let why = 'navigate';
    if (mark === here) {
      window.sessionStorage.removeItem(RETURN_KEY);
      saved = readWin();
      why = 'mark';
    } else if (shouldRestore()) {
      saved = readWin();
      why = 'back/reload';
    }
    console.debug('[nx] return-scroll: 도착', here, 'mark=', mark, 'path=', why,
      'saved=', saved ? `${saved.top}/${(saved.els || []).length}els` : 'none');
    if (saved !== null) {
      restoreWindow(saved.top);
      if (saved.els) restoreEls(saved.els);
    }
  } catch {
    /* 무시 */
  }
}
